/**
 * React Query hooks for the review-of-merge items (board s1389-1 (b)). The Inbox's section and the
 * sidebar's Inbox count read one key, so the two never disagree. Every act re-reads the list before it
 * settles (the card moves to "Reviewed this week", or back), and an Undo of the merge also marks the
 * decision feeds stale: the revert wrote a new decision.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { decisionKeys } from '@/hooks/data/useDecisionFeedData';
import {
  fetchMergeReviews,
  keepMergeReview,
  undoKeepMergeReview,
  undoMerge,
} from '@/services/mergeReviewService';
import type { MergeReview, MergeReviewFeed } from '@/types/entityReviews/mergeReviews';

export const mergeReviewKeys = {
  all: ['merge-reviews'] as const,
};

export function useMergeReviews() {
  return useQuery<MergeReviewFeed>({
    queryKey: mergeReviewKeys.all,
    queryFn: () => fetchMergeReviews(),
    staleTime: 30_000,
  });
}

/**
 * The feed as the cache holds it NOW. An act's own re-read lands before its per-call callbacks run, so
 * this is what the engine says the item came to (the announcement reads it, never the HTTP status).
 */
export function useLatestMergeReviews(): () => MergeReviewFeed | undefined {
  const qc = useQueryClient();
  return useCallback(() => qc.getQueryData<MergeReviewFeed>(mergeReviewKeys.all), [qc]);
}

export function useKeepMergeReview() {
  const qc = useQueryClient();
  return useMutation<MergeReview, Error, string>({
    mutationFn: (reviewId) => keepMergeReview(reviewId),
    onSettled: () => qc.invalidateQueries({ queryKey: mergeReviewKeys.all }),
  });
}

export function useUndoKeepMergeReview() {
  const qc = useQueryClient();
  return useMutation<MergeReview, Error, string>({
    mutationFn: (reviewId) => undoKeepMergeReview(reviewId),
    onSettled: () => qc.invalidateQueries({ queryKey: mergeReviewKeys.all }),
  });
}

/** Variables: the review item (its id keys the busy state; its merge is what the lane reverts). */
export function useUndoMerge() {
  const qc = useQueryClient();
  return useMutation<void, Error, MergeReview>({
    mutationFn: (review) => undoMerge(review.merge_decision_id),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: mergeReviewKeys.all }),
        qc.invalidateQueries({ queryKey: decisionKeys.all }),
      ]),
  });
}
